import React from 'react';
import { Box, Stack, Typography } from '@mui/material';

import { INDETERMINATE, IndeterminateValue, PropertySelectField } from '@paragon/client-tools';
import { WorkLineScope } from '@paragon/contract-library';

import { ensureLevelIsVisible } from '../../../../store/actions/levelVisibilityActions';
import { getLayoutHierarchyAccessLevel } from '../../../../store/selectors/layoutModeSelectors';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { useBuildingLevelHierarchy } from './useBuildingLevelHierarchy';

interface HierarchyOption {
  readonly guid: string;
  readonly name: string;
}

const allIdentifier: HierarchyOption = { name: 'All', guid: '<All>' };

export function WorkLineScopeSelect({
  value,
  onChange,
  disabled,
}: {
  readonly value: WorkLineScope | IndeterminateValue;
  readonly onChange: (scope: WorkLineScope) => void;
  readonly disabled?: boolean;
}) {
  const dispatch = useAppDispatch();

  const { buildings, levels, buildingsWithLevels, levelsIn, buildingContaining } = useBuildingLevelHierarchy();
  const hierarchyAccess = useAppSelector(getLayoutHierarchyAccessLevel);

  if (!hierarchyAccess.read) return null;

  const selectedBuildingGuid =
    value === INDETERMINATE || value.type === 'Project'
      ? null
      : value.type === 'Building'
        ? value.buildingGuid
        : buildingContaining(value.levelGuid);

  const buildingOptions = [allIdentifier].concat(buildingsWithLevels);
  const levelOptions = [allIdentifier].concat(levelsIn(selectedBuildingGuid));

  const buildingValue =
    value === INDETERMINATE ? INDETERMINATE : selectedBuildingGuid ? buildings[selectedBuildingGuid] : allIdentifier;
  const levelValue =
    value === INDETERMINATE ? INDETERMINATE : value.type === 'Level' ? levels[value.levelGuid] : allIdentifier;

  function handleBuildingChange(buildingOption: HierarchyOption) {
    if (buildingOption.guid === allIdentifier.guid) {
      onChange({ type: 'Project' });
      return;
    }

    const [firstLevel] = levelsIn(buildingOption.guid);
    if (firstLevel != null) dispatch(ensureLevelIsVisible(firstLevel.guid, { makeActive: true }));

    onChange({ type: 'Building', buildingGuid: buildingOption.guid });
  }

  function handleLevelChange(levelOption: HierarchyOption) {
    if (levelOption.guid === allIdentifier.guid) {
      if (selectedBuildingGuid != null) onChange({ type: 'Building', buildingGuid: selectedBuildingGuid });
      return;
    }

    onChange({ type: 'Level', levelGuid: levelOption.guid });
    dispatch(ensureLevelIsVisible(levelOption.guid, { makeActive: true }));
  }

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ marginBottom: 2 }}>
        Visibility
      </Typography>

      <Stack direction="row" spacing={1}>
        <PropertySelectField<HierarchyOption>
          property="Building"
          value={buildingValue}
          options={buildingOptions}
          getName={(option) => option.name}
          getValue={(option) => option.guid}
          submit={handleBuildingChange}
          margin="none"
          disabled={disabled || !hierarchyAccess.write}
        />

        <PropertySelectField<HierarchyOption>
          property="Level"
          value={levelValue}
          options={levelOptions}
          getName={(option) => option.name}
          getValue={(option) => option.guid}
          submit={handleLevelChange}
          margin="none"
          disabled={disabled || !hierarchyAccess.write || selectedBuildingGuid == null}
        />
      </Stack>
    </Box>
  );
}
